import React, { useCallback, useEffect, useState } from 'react'
import {
    Badge,
    Button,
    Empty,
    List,
    Popover,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd'
import {
    InboxOutlined,
    ReloadOutlined,
    StopOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    dismissAllReminders,
    dismissReminder,
    getReminderSummary,
    getReminders,
} from '../../../services/reminderService'
import { localeService } from '../../../services/localeService'
import type { ReminderItem, ReminderStatus } from '../../../types/reminder'
import { getDevicePackageDefinition } from '../../../constants/devicePackages'

const { Text } = Typography

const PAGE_SIZE = 10
const REFRESH_INTERVAL_MS = 30 * 1000

interface ReminderCenterProps {
    enabled: boolean
}

const getBadgeCount = (count: number): number | string => {
    if (count > 99) {
        return '99+'
    }
    return count
}

const ReminderCenter: React.FC<ReminderCenterProps> = ({ enabled }) => {
    const { t } = useTranslation('common')
    const [open, setOpen] = useState(false)
    const [loadingSummary, setLoadingSummary] = useState(false)
    const [loadingList, setLoadingList] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [dismissingAll, setDismissingAll] = useState(false)
    const [dismissingKeys, setDismissingKeys] = useState<Set<string>>(
        () => new Set()
    )
    const [unreadCount, setUnreadCount] = useState(0)
    const [items, setItems] = useState<ReminderItem[]>([])
    const [total, setTotal] = useState(0)

    const refreshSummary = useCallback(async () => {
        if (!enabled) {
            setUnreadCount(0)
            return
        }

        setLoadingSummary(true)
        try {
            const summary = await getReminderSummary()
            setUnreadCount(summary.unread_count)
        } catch (error) {
            console.error('获取提醒数量失败', error)
        } finally {
            setLoadingSummary(false)
        }
    }, [enabled])

    const refreshList = useCallback(async () => {
        if (!enabled) {
            setItems([])
            setTotal(0)
            return
        }

        setLoadingList(true)
        try {
            const response = await getReminders({
                limit: PAGE_SIZE,
                offset: 0,
            })
            setItems(response.items)
            setTotal(response.total)
            setUnreadCount(response.total)
        } catch (error) {
            console.error('获取提醒列表失败', error)
        } finally {
            setLoadingList(false)
        }
    }, [enabled])

    const loadMore = async () => {
        if (!enabled || items.length >= total) {
            return
        }

        setLoadingMore(true)
        try {
            const response = await getReminders({
                limit: PAGE_SIZE,
                offset: items.length,
            })
            setItems((current) => [...current, ...response.items])
            setTotal(response.total)
            setUnreadCount(response.total)
        } catch (error) {
            console.error('加载更多提醒失败', error)
        } finally {
            setLoadingMore(false)
        }
    }

    useEffect(() => {
        if (!enabled) {
            setUnreadCount(0)
            setItems([])
            setTotal(0)
            return
        }

        void refreshSummary()
        const intervalId = window.setInterval(() => {
            void refreshSummary()
        }, REFRESH_INTERVAL_MS)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [enabled, refreshSummary])

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen)
        if (nextOpen) {
            void refreshList()
        }
    }

    const handleDismiss = async (item: ReminderItem) => {
        setDismissingKeys((current) => {
            const next = new Set(current)
            next.add(item.dismiss_key)
            return next
        })

        try {
            const response = await dismissReminder({
                reminder_type: item.reminder_type,
                target_type: item.target_type,
                target_id: item.target_id,
                dismiss_key: item.dismiss_key,
                status: item.status,
                reminder_at: item.reminder_at,
            })
            if (response.dismissed_count === 0) {
                await refreshList()
                return
            }
            setItems((current) =>
                current.filter(
                    (candidate) => candidate.dismiss_key !== item.dismiss_key
                )
            )
            setTotal((current) => Math.max(0, current - 1))
            setUnreadCount((current) => Math.max(0, current - 1))
        } catch (error) {
            console.error('忽略提醒失败', error)
        } finally {
            setDismissingKeys((current) => {
                const next = new Set(current)
                next.delete(item.dismiss_key)
                return next
            })
        }
    }

    const handleDismissAll = async () => {
        setDismissingAll(true)
        try {
            const response = await dismissAllReminders()
            if (response.dismissed_count > 0) {
                setItems([])
                setTotal(0)
                setUnreadCount(0)
            } else {
                await refreshList()
            }
        } catch (error) {
            console.error('全部忽略提醒失败', error)
        } finally {
            setDismissingAll(false)
        }
    }

    const getStatusTag = (status: ReminderStatus) => {
        if (status === 'expired') {
            return <Tag color="error">{t('reminders.status.expired')}</Tag>
        }

        return <Tag color="warning">{t('reminders.status.warning')}</Tag>
    }

    const getTargetName = (item: ReminderItem) =>
        item.target_name || item.target_address || item.target_id

    const getPackageLabel = (item: ReminderItem) => {
        if (!item.package_key) {
            return null
        }

        const packageDefinition = getDevicePackageDefinition(item.package_key)
        return t(packageDefinition.labelI18nKey, {
            ns: 'device',
            defaultValue: item.package_key,
        })
    }

    const getReminderMessage = (item: ReminderItem) => {
        const name = getTargetName(item)
        if (item.reminder_type === 'network_service_due') {
            const packageLabel = getPackageLabel(item)

            const interpolation = {
                name,
                count: Math.abs(item.days_remaining),
                packageLabel,
            }

            if (item.status === 'expired') {
                return t('reminders.network.expiredMessage', interpolation)
            }

            if (item.days_remaining === 0) {
                return t('reminders.network.todayDueMessage', interpolation)
            }

            return t('reminders.network.warningMessage', interpolation)
        }

        if (item.status === 'expired') {
            return t('reminders.lure.expiredMessage', {
                name,
                count: Math.abs(item.days_remaining),
            })
        }

        if (item.days_remaining === 0) {
            return t('reminders.lure.todayDueMessage', {
                name,
            })
        }

        return t('reminders.lure.warningMessage', {
            name,
            count: item.days_remaining,
        })
    }

    const getReminderDateLabel = (item: ReminderItem) =>
        item.reminder_type === 'network_service_due'
            ? t('reminders.network.reminderAt', {
                  date: localeService.formatDateOnly(item.reminder_at),
              })
            : t('reminders.lure.reminderAt', {
                  date: localeService.formatDateOnly(item.reminder_at),
              })

    const stopScrollPropagation = (
        event:
            | React.WheelEvent<HTMLDivElement>
            | React.TouchEvent<HTMLDivElement>
    ) => {
        event.stopPropagation()
    }

    const content = (
        <div
            className="reminder-center-panel"
            onWheel={stopScrollPropagation}
            onTouchMove={stopScrollPropagation}
        >
            <div className="reminder-center-header">
                <Space direction="vertical" size={0}>
                    <Text strong>{t('reminders.title')}</Text>
                    <Text type="secondary" className="reminder-center-subtitle">
                        {t('reminders.unreadCount', { count: total })}
                    </Text>
                </Space>
                <Space>
                    <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={refreshList}
                        loading={loadingList}
                    >
                        {t('buttons.refresh')}
                    </Button>
                    <Button
                        type="text"
                        size="small"
                        icon={<StopOutlined />}
                        onClick={handleDismissAll}
                        loading={dismissingAll}
                        disabled={total === 0}
                    >
                        {t('reminders.dismissAll')}
                    </Button>
                </Space>
            </div>

            {loadingList ? (
                <div className="reminder-center-loading">
                    <Spin />
                </div>
            ) : items.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('reminders.empty')}
                />
            ) : (
                <List
                    size="small"
                    dataSource={items}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Button
                                    key="dismiss"
                                    type="link"
                                    size="small"
                                    onClick={() => handleDismiss(item)}
                                    loading={dismissingKeys.has(
                                        item.dismiss_key
                                    )}
                                >
                                    {t('reminders.dismiss')}
                                </Button>,
                            ]}
                        >
                            <Space
                                direction="vertical"
                                size={4}
                                className="reminder-center-item"
                            >
                                <div className="reminder-center-item-title">
                                    <Text strong>
                                        {getTargetName(item)}
                                    </Text>
                                    {getStatusTag(item.status)}
                                </div>
                                <Text type="secondary">
                                    {getReminderMessage(item)}
                                </Text>
                                <Text
                                    type="secondary"
                                    className="reminder-center-date"
                                >
                                    {getReminderDateLabel(item)}
                                </Text>
                            </Space>
                        </List.Item>
                    )}
                    loadMore={
                        items.length < total ? (
                            <div className="reminder-center-load-more">
                                <Button
                                    type="link"
                                    onClick={loadMore}
                                    loading={loadingMore}
                                >
                                    {t('reminders.loadMore')}
                                </Button>
                            </div>
                        ) : undefined
                    }
                />
            )}
        </div>
    )

    if (!enabled) {
        return null
    }

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
        >
            <Badge count={getBadgeCount(unreadCount)} size="small">
                <Button
                    type="text"
                    aria-label={t('reminders.open')}
                    icon={<InboxOutlined />}
                    loading={loadingSummary}
                    className="reminder-center-button"
                />
            </Badge>
        </Popover>
    )
}

export default ReminderCenter
